import type { AxiosRequestConfig } from 'axios';
import { fetch } from 'http-common';

import { PostData } from 'types';
import {
  AddOrderItemPayload,
  CheckProductStatusResponse,
  OrderCreationResponse,
  OrderHeaderResponse,
  OrderItem,
  OrderItemMutationResponse,
  OrderUpdatePayload,
  Takeover,
} from 'types/api/orders.type';

type OrderParams = {
  warehouse: string;
  season: string;
  zone: string;
  scale: string;
  oldItemColorCode: string;
  newItemColorCode: string;
  itemCode: string;
  mainFabricCode: string;
  deliveryCode: string;
  takeoverCode: string;
  quantities: number[];
};

class OrdersDataService {
  private http = fetch();

  private ep = '/v1/orders';

  // Constants
  public getTakeovers() {
    return this.http.get<Takeover[]>(`${this.ep}/constants/takeovers`);
  }

  /**
   * Checks whether a product (colour variant) is in the given order.
   *
   * Endpoint: GET /v1/orders/{orderId}/items/check
   * Query params: productCode (required), colorCode (required), mainFabricCode (optional)
   *
   * Returns { inOrder: false } when not present, or
   * { inOrder: true, itemId, sizes } when present.
   */
  public checkProductStatus(
    orderId: string,
    productCode: string,
    colorCode: string,
    mainFabricCode?: string
  ) {
    return this.http.get<CheckProductStatusResponse>(`${this.ep}/${orderId}/items/check`, {
      params: {
        productCode,
        colorCode,
        ...(mainFabricCode ? { mainFabricCode } : {}),
      },
    });
  }

  public getScales() {
    return this.http.get(`${this.ep}/constants/scale`);
  }

  // Order info
  public getOrderById(orderId: string) {
    return this.http.get<OrderHeaderResponse>(`${this.ep}/${orderId}`);
  }

  public post(
    season: string,
    zone: string,
    warehouse: string,
    customerCode: string,
    collectionCode?: string,
    /** Shipping Address / Goods Destination picked in the New Order modal (optional). */
    addresses: { shippingAddressCode?: string; goodsDestinationCode?: string } = {}
  ) {
    return this.http.post<OrderCreationResponse>(this.ep, {
      season,
      zone,
      warehouseInternalCode: warehouse || '',
      customerCode,
      collectionCode: collectionCode || '',
      ...addresses,
    });
  }

  public put(orderId: string, data: OrderUpdatePayload, requestConfig?: AxiosRequestConfig) {
    return this.http.put(`${this.ep}/${orderId}`, data, requestConfig);
  }

  public deleteOrder(orderId: string) {
    return this.http.delete(`${this.ep}/${orderId}`);
  }

  // Items (products)
  /**
   * Adds a single product (colour variant) to an order.
   *
   * Endpoint: POST /v1/orders/{orderId}/items
   * Returns the created {@link OrderItem}.
   */
  public addItem(orderId: string, data: AddOrderItemPayload) {
    return this.http.post<OrderItem>(`${this.ep}/${orderId}/items`, data);
  }

  // deprecated endpoint, used on selections page
  public postMultipleProducts(orderNumber: string, data: PostData) {
    return this.http.post(`${this.ep}/${orderNumber}/products/multiple`, data);
  }

  // deprecated endpoint
  public putQuantities(orderNumber: string, data: OrderParams) {
    return this.http.put(`${this.ep}/${orderNumber}/products`, data);
  }

  /**
   * Updates a single size quantity for a colour variant.
   *
   * Endpoint: PATCH /v1/orders/{orderId}/items/{itemId}
   * Body: { sizes: [{ size, quantity }] }
   * Returns the updated {@link OrderItem} along with the recomputed order-level
   * totals: { item, orderTotalQuantity, orderTotalPrice }.
   */
  public updateItemQuantity(orderId: string, itemId: string, size: string, quantity: number) {
    const encodedItem = encodeURIComponent(itemId);
    return this.http.patch<OrderItemMutationResponse>(
      `${this.ep}/${orderId}/items/${encodedItem}`,
      { sizes: [{ size, quantity }] }
    );
  }

  /**
   * Removes an entire item (single colour variant of one style) from an order.
   *
   * Endpoint: DELETE /v1/orders/{orderId}/items/{itemId}
   * Returns the deleted {@link OrderItem} along with the recomputed order-level
   * totals: { item, orderTotalQuantity, orderTotalPrice }.
   */
  public deleteItem(orderId: string, itemId: string) {
    const encodedItem = encodeURIComponent(itemId);
    return this.http.delete<OrderItemMutationResponse>(
      `${this.ep}/${orderId}/items/${encodedItem}`
    );
  }

  // Actions
  public duplicate(orderId: string, season: string, zone: string, customerCode: string) {
    return this.http.post<OrderCreationResponse>(`${this.ep}/${orderId}/duplicate`, {
      season,
      zone,
      customerCode,
    });
  }

  public export(orderId: string, lang: string, withImages = true, priceType?: string) {
    return this.http.get<{ csv: string; imageUrls: string[] }>(
      `${this.ep}/${orderId}/export`,
      { params: { lang, withImages, ...(priceType ? { priceType } : {}) } }
    );
  }

  public getImportTemplate(orderId: string, params?: { gender?: string; category?: string }) {
    return this.http.get<Blob>(`${this.ep}/${orderId}/import/template`, {
      responseType: 'blob',
      params: {
        ...(params?.gender ? { gender: params.gender } : {}),
        ...(params?.category ? { category: params.category } : {}),
      },
    });
  }

  public importOrder(orderId: string, data: FormData) {
    return this.http.post<OrderHeaderResponse>(`${this.ep}/${orderId}/import`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

const ordersDataService = new OrdersDataService();

export { ordersDataService };
